import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AssignmentOutlined as AssignmentIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

import { Authorize, NavigateLinks } from '@/components';
import { BUSINESS_ROLE_GROUPS, ROLES } from '@/components/security/AppRoles';
import { commercePageBreadcrumbs } from './config/breadcrumbs';
import { useGetCommerceByIdQuery } from './services';
import {
  INTEGRATION_REQUEST_ROLES,
  INTEGRATION_REQUESTS_PATH,
} from './pages/IntegrationRequests';

const CommerceDashboard = () => {
  const navigate = useNavigate();
  const { commerceId } = useParams<{ commerceId: string }>();

  if (!commerceId) {
    throw new Error('El ID del comercio es requerido');
  }

  const { data: commerceData } = useGetCommerceByIdQuery(
    { commerceId },
    { skip: !commerceId }
  );

  const breadcrumbs = useMemo(
    () => [
      ...commercePageBreadcrumbs(navigate),
      {
        label: commerceData?.name || 'Comercio',
        onClick: () => navigate(`/business/commerce/${commerceId}/dashboard`),
      },
    ],
    [navigate, commerceId, commerceData?.name]
  );

  return (
    <section className='w-full h-full flex flex-col'>
      <NavigateLinks
        actualPage={`Dashboard - ${commerceData?.name || 'Comercio'}`}
        breadcrumbs={breadcrumbs}
      />
      <ul className='w-full h-full flex flex-wrap justify-center content-center gap-[50px] mx-auto'>
        <Authorize permission={ROLES.BUSINESS.admin} fallback={null}>
          <li
            className='w-[200px] p-[25px] aspect-[4/3] flex flex-col justify-between items-center bg-white rounded-[5px] shadow-[0_4px_4px_rgba(0,0,0,0.1)] transition-transform duration-[0.25s] ease-in-out cursor-pointer overflow-hidden hover:scale-105'
            onClick={() => navigate(`/business/commerce/${commerceId}/settings`)}
          >
            <SettingsIcon
              fontSize='large'
              className='h-[65%] object-scale-down'
            />
            <p className='text-base text-center overflow-hidden text-ellipsis whitespace-nowrap'>
              Configuraciones
            </p>
          </li>
        </Authorize>
        <li
          className='w-[200px] p-[25px] aspect-[4/3] flex flex-col justify-between items-center bg-white rounded-[5px] shadow-[0_4px_4px_rgba(0,0,0,0.1)] transition-transform duration-[0.25s] ease-in-out cursor-pointer overflow-hidden hover:scale-105'
          onClick={() => navigate(`/business/commerce/${commerceId}/branches`)}
        >
          <StoreIcon fontSize='large' className='h-[65%] object-scale-down' />
          <p className='text-base text-center overflow-hidden text-ellipsis whitespace-nowrap'>
            Sucursales
          </p>
        </li>
        <Authorize
          permission={BUSINESS_ROLE_GROUPS.withoutDispatcher}
          fallback={null}
        >
          <li
            className='w-[200px] p-[25px] aspect-[4/3] flex flex-col justify-between items-center bg-white rounded-[5px] shadow-[0_4px_4px_rgba(0,0,0,0.1)] transition-transform duration-[0.25s] ease-in-out cursor-pointer overflow-hidden hover:scale-105'
            onClick={() =>
              navigate('/business/commerce/included-pickup-commerces')
            }
          >
            <StorefrontIcon
              fontSize='large'
              className='h-[65%] object-scale-down'
            />
            <p className='text-base text-center overflow-hidden text-ellipsis whitespace-nowrap'>
              Comercios Incluidos
            </p>
          </li>
        </Authorize>
        <Authorize permission={INTEGRATION_REQUEST_ROLES} fallback={null}>
          <li
            className='w-[200px] p-[25px] aspect-[4/3] flex flex-col justify-between items-center bg-white rounded-[5px] shadow-[0_4px_4px_rgba(0,0,0,0.1)] transition-transform duration-[0.25s] ease-in-out cursor-pointer overflow-hidden hover:scale-105'
            onClick={() =>
              navigate(`${INTEGRATION_REQUESTS_PATH}/new?commerceId=${commerceId}`)
            }
          >
            <AssignmentIcon fontSize='large' className='h-[65%] object-scale-down' />
            <p className='text-base text-center overflow-hidden text-ellipsis whitespace-nowrap'>
              Solicitar integración (API)
            </p>
          </li>
        </Authorize>
      </ul>
    </section>
  );
};

export default CommerceDashboard;
